/**
 * iter741 — Admin tool: upload a new Android APK build straight from
 * Settings → App Updates. Replaces the current
 * `/app/frontend/public/downloads/smartoerp-android.apk` and updates
 * `apk-info.json` so the landing-page badge + Cashier install card
 * immediately reflect the new version.
 *
 * Admin role required (backend enforces). Non-admins see a polite
 * "Admin only" message instead of the upload form.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Upload, Trash2, RefreshCw, CheckCircle2, AlertTriangle, Download, FileWarning } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

function _fmtKb(kb) {
  if (!kb) return '—';
  if (kb < 1024) return `${kb} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export default function ApkAdminCard() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  // Upload form state
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const fetchInfo = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/apk/info');
      setInfo(data);
      setDenied(false);
      // Pre-fill version from the previous build so the next bump
      // (e.g. 0.1.0 → 0.2.0) is just one keystroke away.
      if (!version && data?.version) {
        setVersion(data.version);
      }
    } catch (e) {
      if (e?.response?.status === 403) {
        setDenied(true);
      } else {
        toast.error(e?.response?.data?.detail || 'Could not load APK info.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInfo();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Pick the .apk file first.');
      return;
    }
    if (!/^\d+\.\d+\.\d+/.test(version.trim())) {
      toast.error('Use a semver version like 0.2.0 or 1.4.7.');
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const form = new FormData();
      form.append('apk', file, file.name || 'smartoerp-android.apk');
      form.append('version', version.trim());
      form.append('notes', notes.trim());
      const { data } = await api.post('/admin/apk/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev) => {
          if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
        },
      });
      toast.success(data?.msg || `APK ${version} uploaded.`);
      setFile(null);
      setNotes('');
      setProgress(0);
      await fetchInfo();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const onClear = async () => {
    if (!confirm('Delete the current APK and metadata? The landing-page download badge will hide until you upload a new build.')) return;
    try {
      await api.post('/admin/apk/clear');
      toast.success('APK cleared.');
      await fetchInfo();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Clear failed.');
    }
  };

  if (denied) {
    return (
      <Card className="border-rose-200 bg-rose-50/40" data-testid="apk-admin-card-denied">
        <CardContent className="p-5 flex items-start gap-3">
          <AlertTriangle className="text-rose-600 shrink-0 mt-0.5" size={18} />
          <div className="text-sm text-rose-900">
            <strong>Admin role required.</strong> APK uploads can only be performed by users with the
            <code className="mx-1 px-1 bg-white border border-rose-200 rounded">admin</code>,
            <code className="mx-1 px-1 bg-white border border-rose-200 rounded">owner</code>, or
            <code className="mx-1 px-1 bg-white border border-rose-200 rounded">super_admin</code> role.
            Ask the workspace owner to update your role from Users → Edit.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-indigo-200" data-testid="apk-admin-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-outfit flex items-center gap-2">
          <Upload size={18} className="text-indigo-600" />
          Update APK
          <Badge variant="outline" className="text-[10px] border-indigo-400 text-indigo-700">ADMIN</Badge>
        </CardTitle>
        <CardDescription>
          Publish a new signed Android <code>.apk</code> build for cashiers and managers to sideload.
          The landing-page download badge and the Cashier install card both read from this metadata.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current state */}
        <div className="rounded-lg border bg-stone-50 p-3 text-sm" data-testid="apk-current-info">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="font-semibold text-stone-800">Currently published</div>
            <Button
              size="sm"
              variant="ghost"
              onClick={fetchInfo}
              disabled={loading}
              data-testid="apk-refresh-btn"
              className="h-7 text-[11px]"
            >
              <RefreshCw size={12} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
          {loading ? (
            <div className="text-[12px] text-stone-500 mt-1">Loading…</div>
          ) : info ? (
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[12px]">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">Version</div>
                <div className="font-mono font-semibold" data-testid="apk-current-version">{info.version || '—'}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">Build date</div>
                <div className="font-mono">{info.build_date || '—'}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">Size</div>
                <div className="font-mono">{_fmtKb(info.size_kb)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">Status</div>
                <div className="flex items-center gap-1">
                  {info.available && info.file_present ? (
                    <><CheckCircle2 size={13} className="text-emerald-600" /><span className="text-emerald-700 font-semibold">Live</span></>
                  ) : (
                    <><FileWarning size={13} className="text-amber-600" /><span className="text-amber-700">Not published</span></>
                  )}
                </div>
              </div>
              {info.notes && (
                <div className="col-span-2 sm:col-span-4 text-[11px] text-stone-600 border-t pt-2 mt-1">
                  <span className="font-semibold">Release notes:</span> {info.notes}
                </div>
              )}
              {info.uploaded_by && (
                <div className="col-span-2 sm:col-span-4 text-[10px] text-stone-500">
                  Uploaded by {info.uploaded_by} {info.uploaded_at ? `· ${info.uploaded_at.replace('T', ' ').slice(0, 19)} UTC` : ''}
                </div>
              )}
            </div>
          ) : (
            <div className="text-[12px] text-stone-500 mt-1">No metadata available.</div>
          )}
          {info?.available && info?.file_present && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <a
                href="https://www.smartoerp.com/api/downloads/apk/latest"
                download="smartoerp-android.apk"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-indigo-700 hover:underline"
                data-testid="apk-download-current-btn"
              >
                <Download size={12} /> Download current APK
              </a>
              <Button
                size="sm"
                variant="ghost"
                onClick={onClear}
                className="h-6 text-[10px] text-rose-600 hover:bg-rose-50 ml-auto"
                data-testid="apk-clear-btn"
              >
                <Trash2 size={11} className="mr-1" /> Clear / unpublish
              </Button>
            </div>
          )}
        </div>

        {/* Upload form */}
        <form onSubmit={onSubmit} className="space-y-3" data-testid="apk-upload-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="apk-version">New version <span className="text-rose-600">*</span></Label>
              <Input
                id="apk-version"
                placeholder="e.g., 0.2.0"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                required
                pattern="^\d+\.\d+\.\d+.*$"
                data-testid="apk-version-input"
              />
              <p className="text-[10px] text-stone-500 mt-1">
                Semver only. Same value will appear in the landing-page badge and the cashier install card.
              </p>
            </div>
            <div>
              <Label htmlFor="apk-file">APK file <span className="text-rose-600">*</span></Label>
              <Input
                id="apk-file"
                type="file"
                accept=".apk,application/vnd.android.package-archive"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                required
                data-testid="apk-file-input"
              />
              {file && (
                <p className="text-[10px] text-stone-500 mt-1">
                  Selected: <span className="font-mono">{file.name}</span> · {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              )}
            </div>
          </div>
          <div>
            <Label htmlFor="apk-notes">Release notes (optional)</Label>
            <Input
              id="apk-notes"
              placeholder="What's new in this build?"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="apk-notes-input"
            />
          </div>
          {progress > 0 && progress < 100 && (
            <div className="w-full bg-stone-100 rounded h-2 overflow-hidden" data-testid="apk-upload-progress">
              <div className="bg-indigo-600 h-2 transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              type="submit"
              disabled={uploading || !file || !version}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="apk-upload-submit-btn"
            >
              <Upload size={14} className="mr-1" />
              {uploading ? `Uploading… ${progress}%` : 'Publish APK'}
            </Button>
            <span className="text-[11px] text-stone-500">
              Max 200 MB. File must be a signed .apk (not .aab).
            </span>
          </div>
        </form>

        <div className="text-[11px] text-stone-600 border-t pt-2 leading-relaxed">
          <strong>How this works:</strong> the file replaces
          <code className="mx-1 px-1 bg-stone-100 rounded">/downloads/smartoerp-android.apk</code> on this pod, and
          <code className="mx-1 px-1 bg-stone-100 rounded">/downloads/apk-info.json</code> is updated to point at the new version. After you deploy preview → production, every iPad/Android device hitting the landing page or PrinterSetup gets the new file. No GitHub release required.
        </div>
      </CardContent>
    </Card>
  );
}
